import { useEffect, useState, useCallback } from 'react';
import { getMobileToday, updatePostHuddleAction } from '../../lib/api';
import { CheckCircle, Clock, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MobileLoanDetail from './MobileLoanDetail';

export default function MobileTasks({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFile, setOpenFile] = useState(null);

  const load = useCallback(async () => {
    try { const d = await getMobileToday(user?.initials || ''); setData(d); }
    finally { setLoading(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    try {
      await updatePostHuddleAction(id, { status: 'Completed' });
      toast.success('Completed', { action: { label: 'Undo', onClick: async () => { await updatePostHuddleAction(id, { status: 'Pending' }); load(); } } });
      load();
    } catch (e) { toast.error('Could not complete'); }
  };

  if (loading && !data) return <div className="p-4 space-y-2">{[0,1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</div>;

  const { overdue = [], today = [], upcoming = [] } = data?.today_actions || {};

  return (
    <div className="pb-4" data-testid="mobile-tasks">
      <div className="px-1 pt-2 pb-3">
        <h1 className="text-lg font-bold text-foreground leading-tight">Your tasks</h1>
        <p className="text-[11px] text-muted-foreground">Grouped by when they're due · {overdue.length + today.length + upcoming.length} total</p>
      </div>
      <Group label="Overdue"  tone="critical" items={overdue}  onComplete={complete} onOpenFile={setOpenFile} />
      <Group label="Today"    tone="warn"     items={today}    onComplete={complete} onOpenFile={setOpenFile} />
      <Group label="Upcoming" tone="info"     items={upcoming} onComplete={complete} onOpenFile={setOpenFile} />
      {!overdue.length && !today.length && !upcoming.length && (
        <div className="text-center py-12">
          <CheckCircle size={32} weight="duotone" className="text-[color:var(--mr-ok)] mx-auto mb-2" />
          <p className="text-sm font-bold text-foreground">No open tasks</p>
          <p className="text-xs text-muted-foreground">You're caught up.</p>
        </div>
      )}
      {openFile && <MobileLoanDetail fileId={openFile} onClose={() => setOpenFile(null)} currentUser={user} />}
    </div>
  );
}

function Group({ label, tone, items, onComplete, onOpenFile }) {
  if (!items?.length) return null;
  const toneCls = tone === 'critical' ? 'text-[color:var(--mr-red-strong)]' : tone === 'warn' ? 'text-[color:var(--mr-warn)]' : 'text-muted-foreground';
  const iconCls = tone === 'critical' ? 'text-[color:var(--mr-red)]' : tone === 'warn' ? 'text-[color:var(--mr-warn)]' : 'text-[color:var(--mr-blue)]';
  return (
    <section className="mb-4" data-testid={`task-group-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 px-1 mb-1.5">
        <Clock size={13} weight="fill" className={iconCls} />
        <h2 className={`text-[11px] font-black uppercase tracking-wider ${toneCls}`}>{label}</h2>
        <span className="text-[10px] font-bold text-muted-foreground">· {items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map(a => (
          <div key={a.id} className="mr-card p-2.5 flex items-start gap-2" data-testid={`task-${a.id}`}>
            <button onClick={() => onComplete(a.id)} data-testid={`task-complete-${a.id}`}
              aria-label="Complete task"
              className="w-8 h-8 rounded-full border-2 border-muted-foreground hover:border-[color:var(--mr-ok)] active:bg-[color:var(--mr-ok-wash)] flex-shrink-0 flex items-center justify-center min-w-[32px]">
              <CheckCircle size={16} className="text-[color:var(--mr-ok)] opacity-0 hover:opacity-100" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-bold text-foreground truncate">{a.borrower || 'Task'}</span>
                {a.deadline && <span className="text-[10px] text-muted-foreground">· {a.deadline}</span>}
                {a.assigned_to && <span className="text-[10px] text-muted-foreground">· {a.assigned_to}</span>}
              </div>
              <p className="text-[11px] text-foreground/80 leading-snug line-clamp-2">{a.action}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
